<div [@pageLoad]="pageLoadState">
  <div class="gap-[40px] flex flex-col h-[768px] shrink-0 justify-start items-center pl-[64px] pr-[64px] pt-[40px] pb-[0px] bg-[#f8fafdff] w-[100%] relative">
    <!-- Loading State -->
    <div *ngIf="isLoading" class="flex-1 flex items-center justify-center w-full">
      <span class="text-[#444955ff] font-normal font-['archivo'] text-[18px] leading-[27px]">
        Loading event details...
      </span>
    </div>

    <!-- Error State -->
    <div *ngIf="hasError && !isLoading" class="flex-1 flex flex-col items-center justify-center w-full gap-[16px]">
      <span class="text-[#e74c3c] font-semibold font-['archivo'] text-[18px] leading-[27px]">
        Failed to load event details
      </span>
      <div class="gap-[12px] flex flex-row items-center">
        <NiButton
          variant="outlined"
          color="primary"
          label="Retry"
          [px]="'[24px]'"
          [py]="'[12px]'"
          [rounded]="'[999px]'"
          (onClick)="loadEvent()">
        </NiButton>
        <NiButton
          variant="outlined"
          color="secondary"
          label="Back to Events"
          leadingIcon="arrowNarrowLeft"
          [px]="'[24px]'"
          [py]="'[12px]'"
          [rounded]="'[999px]'"
          (onClick)="onBackClick()">
        </NiButton>
      </div>
    </div>

    <!-- Event Details -->
    <ng-container *ngIf="!isLoading && !hasError && event">
      <div class="gap-[24px] flex flex-col w-[100%] shrink-0 justify-start items-center self-stretch">
        
        <!-- Event Title -->
        <span class="w-[100%] shrink-0 text-[#111215ff] text-center font-bold font-['archivo'] text-[24px] leading-[26px]">
          {{ event.title }}
        </span>
        
        <!-- Tags -->
        <div *ngIf="event.tags && event.tags.length > 0" class="gap-[16px] flex flex-row shrink-0 justify-start items-start flex-wrap">
          <div
            *ngFor="let tag of event.tags"
            class="flex flex-row shrink-0 rounded-[100px] justify-center items-center pl-[12px] pr-[12px] pt-[8px] pb-[8px] bg-[#dcdddeff]">
            <span class="shrink-0 justify-start items-start text-[#111215ff] text-left font-normal font-['archivo'] text-[18px] leading-[20px]">
              {{ tag }}
            </span>
          </div>
        </div>
        
        <!-- Date and Time Summary -->
        <span *ngIf="event.dateTime" class="shrink-0 justify-start items-start text-[#444955ff] text-center font-semibold font-['archivo'] text-[20px] leading-[22px]">
          {{ event.dateTime }}
        </span>
      </div>
      
      <!-- Event Image and Details -->
      <div class="gap-[40px] flex flex-col shrink-0 justify-start items-start grow basis-0 w-[100%]">
        <div class="gap-[28px] flex flex-row shrink-0 justify-center items-start w-[100%]">
          <!-- Event Image -->
          <div
            class="w-[535px] h-[382px] shrink-0 rounded-[16px] justify-start items-start event-image"
            [class.priority]="true"
            [class.loaded]="!event.image || isImageLoaded('main')"
            [class.event-image--empty]="!event.image"
            [style.background-image]="event.image ? ('url(' + getImageUrl('main') + ')') : null"
            style="background-repeat: no-repeat; background-size: cover; background-position: center;">
            <div
              *ngIf="!event.image"
              class="w-full h-full flex items-center justify-center rounded-[16px] bg-[#EEF2F6] text-[#667085] font-['archivo'] text-[18px] font-medium">
              No image
            </div>
          </div>
          
          <!-- Event Details -->
          <div class="gap-[28px] flex flex-col w-[514px] shrink-0 justify-start items-start">
            <!-- Where -->
            <div *ngIf="event.where" class="gap-[8px] flex flex-col w-[100%] shrink-0 justify-start items-start">
              <span class="shrink-0 justify-start items-start text-[#111215ff] text-center font-bold font-['archivo'] text-[18px] capitalize leading-[20px]">
                {{ whereLabel }}
              </span>
              <span class="shrink-0 justify-start items-start text-[#444955ff] text-center font-normal font-['archivo'] text-[18px] leading-[20px]">
                {{ event.where }}
              </span>
            </div>
            
            <!-- When -->
            <div *ngIf="event.dateTime" class="gap-[8px] flex flex-col w-[100%] shrink-0 justify-start items-start">
              <span class="shrink-0 justify-start items-start text-[#111215ff] text-center font-bold font-['archivo'] text-[18px] capitalize leading-[20px]">
                {{ whenLabel }}
              </span>
              <span class="shrink-0 justify-start items-start text-[#444955ff] text-center font-normal font-['archivo'] text-[18px] leading-[20px]">
                {{ event.dateTime ? event.dateTime : "No time specified" }}
              </span>
            </div>
            
            <!-- Center -->
            <div class="gap-[16px] flex flex-col shrink-0 justify-start items-start w-[100%]">
              <div class="gap-[8px] flex flex-col w-[100%] shrink-0 justify-start items-start">
                <span class="shrink-0 justify-start items-start text-[#111215ff] text-center font-bold font-['archivo'] text-[18px] capitalize leading-[20px]">
                  {{ centerLabel }}
                </span>
                <a 
                  *ngIf="event.address" 
                  [href]="getGoogleMapsUrl(event.address)"
                  target="_blank"
                  class="shrink-0 justify-start items-start text-[#444955] text-center font-normal font-['archivo'] text-[18px] leading-[20px] hover:underline cursor-pointer underline">
                  {{ event.address }}
                </a>
                <span
                  *ngIf="!event.address && event.where"
                  class="shrink-0 justify-start items-start text-[#444955ff] text-center font-normal font-['archivo'] text-[18px] leading-[20px]">
                  {{ event.where }}
                </span>
              </div>
              <!-- Map Image -->
              <div
                *ngIf="event.mapImage"
                class="w-[100%] h-[160px] shrink-0 rounded-[24px] justify-start items-start border-[1px] border-[#E5E7EB] overflow-hidden event-image"
                [class.loaded]="isImageLoaded('map')"
                [style.background-image]="'url(' + getImageUrl('map') + ')'"
                style="background-repeat: no-repeat; background-size: cover; background-position: center;">
              </div>

              <!-- Status badges + Register (Figma: Confirmed / Waiting List) -->
              <div class="flex w-full flex-col gap-[12px] mt-[8px]">
                <div class="flex flex-row flex-wrap items-center gap-[10px]">
                  <span
                    *ngIf="isRegistered"
                    class="inline-flex items-center rounded-full bg-[#12B76A] px-[14px] py-[6px] text-white font-['archivo'] font-semibold text-[14px] leading-[18px]">
                    {{ confirmedLabel }}
                  </span>
                  <span
                    *ngIf="isWaitlisted"
                    class="inline-flex items-center rounded-full bg-[#2E90FA] px-[14px] py-[6px] text-white font-['archivo'] font-semibold text-[14px] leading-[18px]">
                    {{ waitingListLabel }}
                  </span>
                </div>

                <span
                  *ngIf="registrationError"
                  class="text-[#e74c3c] text-center font-normal font-['archivo'] text-[16px] leading-[22px]">
                  {{ registrationError }}
                </span>
                <NiButton
                  variant="contained"
                  [color]="hasActiveRegistration ? 'secondary' : 'primary'"
                  size="[20px]"
                  [label]="registerButtonLabel"
                  [px]="'[32px]'"
                  [py]="'[16px]'"
                  [rounded]="'[999px]'"
                  [bg]="hasActiveRegistration ? '[#ffffff]' : '[#f26f24ff]'"
                  [textColor]="hasActiveRegistration ? '#f26f24' : '#ffffff'"
                  [border]="hasActiveRegistration ? '1' : undefined"
                  [borderColor]="hasActiveRegistration ? '[#f26f24ff]' : undefined"
                  [disabled]="isRegistering"
                  (onClick)="onRegisterClick()"
                  [styleClass]="
                    hasActiveRegistration
                      ? 'event-details-action-btn w-full flex flex-row items-center justify-center gap-[12px] rounded-[999px] border border-[#f26f24ff] bg-white text-[#f26f24] font-bold font-[' +
                        `'archivo'] text-[18px] leading-[23px] px-[32px] py-[16px]'`
                      : 'event-details-action-btn w-full flex flex-row items-center justify-center gap-[12px] rounded-[999px] bg-[#f26f24ff] text-[#ffffffff] font-bold font-[' +
                        `'archivo'] text-[18px] leading-[23px] px-[32px] py-[16px]'`">
                </NiButton>
              </div>
            </div>
            
            <!-- Partnership -->
            <div *ngIf="event.partnership" class="gap-[8px] flex flex-col w-[100%] shrink-0 justify-start items-start">
              <span class="shrink-0 justify-start items-start text-[#111215ff] text-center font-bold font-['archivo'] text-[18px] capitalize leading-[20px]">
                Partnership
              </span>
              <span class="shrink-0 justify-start items-start text-[#444955ff] text-center font-normal font-['archivo'] text-[18px] leading-[20px]">
                {{ event.partnership }}
              </span>
            </div>
          </div>
        </div>
        
        <!-- Description -->
        <div class="flex flex-row shrink-0 justify-center items-start w-[100%]">
          <span class="w-[100%] max-w-[1132px] text-[#444955ff] text-center font-normal font-['archivo'] text-[18px] leading-[30px]">
            {{ event.description }}
          </span>
        </div>
      </div>
      
      <!-- Navigation Buttons -->
      <div class="gap-[8px] flex flex-col w-[100%] shrink-0 self-stretch">
        <div class="gap-[8px] flex flex-row w-[100%] shrink-0 justify-start items-center self-stretch">
        <NiButton
          variant="outlined"
          color="secondary"
          size="[20px]"
          [label]="backLabel"
          leadingIcon="arrowNarrowLeft"
          [px]="'[24px]'"
          [py]="'[16px]'"
          [rounded]="'[999px]'"
          border="1"
          borderColor="[#5BA5F5]"
          bg="transparent"
          (onClick)="onBackClick()"
          [styleClass]="
            'flex flex-row items-center gap-[8px] rounded-[999px] border border-[#5BA5F5] text-[#2E90FA] font-bold font-[' +
            `'archivo'] text-[18px] leading-[23px] line-height-[24px]'`">
        </NiButton>
        </div>
      </div>
    </ng-container>
  </div>
</div>
